// MIT License - Copyright (c) 2026 Doug McNall

import { useEffect, useRef } from 'react';
import OverlayGuard from '../utils/system/OverlayGuard';

/**
 * useGlobalHotkey(bindings, options)
 *
 * Single shared window-level keydown listener with the "don't fire while
 * typing in a field / don't fire while a full-screen overlay owns the
 * keyboard" guard baked in once, instead of hand-copied into every
 * component that wants a global hotkey.
 *
 * Consolidates what used to be five independent window.addEventListener
 * call sites (CheatCodeHandler, MainTabbedInterface, two separate
 * listeners in App.js, DeveloperTools.js) — each with its own
 * independently-written (and inconsistently applied) focus/overlay guard.
 * See the 2026-10-07 session: CheatCodeHandler had NO guard at all, which
 * is how Alt+C fired while typing in the alien "talk" input.
 *
 * @param {Object<string, Function>} bindings
 *   Map of key-combo string -> handler. Combo format: modifiers joined
 *   with '+', case-insensitive, bare key last. Examples:
 *     'n', 'escape', 'f1', 'alt+c', 'ctrl+shift+d'
 *   A bare key ('c') requires NO modifiers held — 'alt+c' is a distinct
 *   binding, not a superset match. Handler receives the KeyboardEvent.
 *
 * @param {Object} [options]
 * @param {boolean} [options.ignoreInFields=true] - skip while focus is in
 *   an INPUT/TEXTAREA/contentEditable element.
 * @param {boolean} [options.ignoreWhenOverlayOpen=true] - skip while
 *   OverlayGuard.isOpen() (see OverlayGuard.js).
 * @param {boolean} [options.preventDefault=true] - call
 *   e.preventDefault() on a matched binding before invoking the handler.
 * @param {boolean} [options.enabled=true] - set false to disable the
 *   listener entirely (e.g. a feature-flagged cheat system).
 *
 * @example
 *   useGlobalHotkey({
 *     'alt+c': () => addCredits(500),
 *     'escape': () => closeModal(),
 *   });
 */
function parseCombo(combo) {
  const parts = combo.toLowerCase().split('+');
  const key = parts[parts.length - 1];
  const modifiers = parts.slice(0, -1);
  return {
    key,
    alt: modifiers.includes('alt'),
    ctrl: modifiers.includes('ctrl') || modifiers.includes('control'),
    shift: modifiers.includes('shift'),
    meta: modifiers.includes('meta') || modifiers.includes('cmd'),
  };
}

function comboMatches(parsed, e) {
  if (e.key.toLowerCase() !== parsed.key) return false;
  return (
    e.altKey === parsed.alt &&
    e.ctrlKey === parsed.ctrl &&
    e.shiftKey === parsed.shift &&
    e.metaKey === parsed.meta
  );
}

export default function useGlobalHotkey(bindings, options = {}) {
  const {
    ignoreInFields = true,
    ignoreWhenOverlayOpen = true,
    preventDefault = true,
    enabled = true,
  } = options;

  // Ref so a new bindings object every render doesn't require tearing
  // down and re-registering the window listener.
  const bindingsRef = useRef(bindings);
  useEffect(() => {
    bindingsRef.current = bindings;
  }, [bindings]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e) => {
      if (!e.key) return;

      if (ignoreInFields) {
        const t = e.target;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
          return;
        }
      }
      if (ignoreWhenOverlayOpen && OverlayGuard.isOpen()) return;

      const current = bindingsRef.current || {};
      for (const combo of Object.keys(current)) {
        if (comboMatches(parseCombo(combo), e)) {
          const handler = current[combo];
          if (handler) {
            if (preventDefault) e.preventDefault();
            handler(e);
          }
          break; // Only one binding fires per keypress.
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, ignoreInFields, ignoreWhenOverlayOpen, preventDefault]);
}
